import {
  Accordion,
  Box,
  Button,
  HStack,
  Image,
  Input,
  Skeleton,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react"
import { embedURL } from "@pathwright/media-utils"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import {
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_TEXTAREA_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Field from "@pathwright/ux/src/components/form/Field"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import MediaManager from "@pathwright/features/src/modules/media-manager/MediaManager"
import { Form, FormikProps, useFormikContext } from "formik"
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CoverPageResourceFragment as CoverPageResource,
  useUpdateCoverPageResourceMutation
} from "@pathwright/graphql-client/src/generated"
import { RESOURCE_TYPE_LABELS } from "../../resource/constants"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import {
  getCategoryPositioningContext,
  getContributorPositioningContext
} from "../positioning"
import CoverPageTrailer from "../trailer/CoverPageTrailer"
import { useCoverPageTranslate } from "../utils"
import { CoverPageBackgroundSettingsPopover } from "./CoverPageBackgroundSettingsPopover"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import {
  getCoverPageAmbientResourceFields,
  getCoverPageBackgroundImageInput,
  getCoverPageBackgroundTypeInput,
  getCoverPageDisplayImage,
  getCoverPagePristineBackgroundImage,
  isCoverPageResolvedBackgroundImageUrl,
  useCoverPagePanelErrorMessage
} from "./utils"

const PATH_TITLE_MIN_LENGTH = 3

const headerSchema = z.object({
  background_image: z.string(),
  is_ambient_background: z.boolean(),
  background_overlay: z.string(),
  cover_image: z.string(),
  name: z.string(),
  short_description: z.string(),
  trailer_embed_code: z.string()
})

type CoverPagePanelHeaderValues = z.infer<typeof headerSchema>

type MediaTriggerRenderProps = {
  isLoading: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
}

function HeaderCoverImagePreview({
  alt,
  image
}: {
  alt: string
  image: string
}) {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(false)
  }, [image])

  return (
    <Box
      position="relative"
      aspectRatio={4 / 3}
      w="100%"
      overflow="hidden"
      borderRadius="inherit"
      bg="blackAlpha.100"
    >
      <Skeleton
        loading={!isLoaded}
        position="absolute"
        inset={0}
        w="full"
        h="full"
        css={{
          "--start-color": "colors.blackAlpha.100",
          "--end-color": "colors.blackAlpha.300"
        }}
      />
      {image ? (
        <Image
          src={image}
          alt={alt}
          w="100%"
          h="100%"
          objectFit="cover"
          display="block"
          opacity={isLoaded ? 1 : 0}
          transition="opacity 120ms ease"
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
        />
      ) : null}
    </Box>
  )
}

function buildInitialValues(
  resource: CoverPageResource
): CoverPagePanelHeaderValues {
  return {
    background_image: getCoverPageBackgroundImageInput(resource),
    is_ambient_background: !!resource.is_ambient_background,
    background_overlay: resource.background_overlay || "",
    cover_image: resource.image_raw || "",
    name: resource.name || "",
    short_description: resource.short_description || "",
    trailer_embed_code: resource.trailer_embed_code || ""
  }
}

function HeaderMediaTrigger({
  contextKey,
  onChoose,
  renderTrigger
}: {
  contextKey: string
  onChoose: (url: string) => void
  renderTrigger: (props: MediaTriggerRenderProps) => React.ReactNode
}) {
  return (
    <MediaManager
      contextKey={contextKey}
      mediaType="image"
      pickOptions={{ maxFiles: 1 }}
      onChooseMedia={(media: { url: string }) => onChoose(media.url)}
      renderLoading={() => null}
      renderButton={({
        onClick
      }: {
        onClick?: React.MouseEventHandler<HTMLElement>
      }) =>
        renderTrigger({
          isLoading: false,
          onClick
        })
      }
    />
  )
}

function HeaderTrailerSection() {
  const form = useFormikContext<CoverPagePanelHeaderValues>()
  const isEditable = useCoverPageIsEditable()
  const [trailerUrl, setTrailerUrl] = useState("")
  const [isResolvingUrl, setIsResolvingUrl] = useState(false)
  const [editorModeOverride, setEditorModeOverride] = useState<
    "url" | "embed" | null
  >(null)
  const [isReplacingTrailer, setIsReplacingTrailer] = useState(false)
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const wasSubmittingRef = useRef(false)
  const { t } = useCoverPageTranslate()

  const hasTrailerUrl = !!trailerUrl.trim()
  const hasTrailer = !!form.values.trailer_embed_code?.trim()
  const isEmbedReadOnly = hasTrailer && !isReplacingTrailer
  const defaultEditorMode = hasTrailerUrl ? "url" : hasTrailer ? "embed" : "url"
  const visibleEditorMode = editorModeOverride || defaultEditorMode

  useEffect(() => {
    if (wasSubmittingRef.current && !form.isSubmitting && !form.dirty) {
      setIsAccordionOpen(false)
      setEditorModeOverride(null)
      setIsReplacingTrailer(false)
    }

    wasSubmittingRef.current = form.isSubmitting
  }, [form.dirty, form.isSubmitting])

  const applyTrailerUrl = async () => {
    const trimmedUrl = trailerUrl.trim()

    if (!trimmedUrl || !isEditable) return

    setIsResolvingUrl(true)

    try {
      const result = (await embedURL(trimmedUrl)) as { html?: string }
      const embedCode = result?.html?.trim()

      if (!embedCode) {
        throw new Error(
          t("panel_header.no_embed_code_found", {
            defaultValue: "No embed code found for this URL."
          })
        )
      }

      await form.setFieldValue("trailer_embed_code", embedCode)
      setEditorModeOverride(null)
      setIsReplacingTrailer(false)
    } catch {
      toaster.create({
        title: t("panel_header.embed_video_url_error", {
          defaultValue:
            "Couldn’t embed that video URL. Try a different URL or paste embed code instead."
        }),
        type: "error",
        duration: 4000,
        closable: true
      })
    } finally {
      setIsResolvingUrl(false)
    }
  }

  return (
    <VStack align="stretch" gap={3}>
      {hasTrailer ? (
        <Box
          borderRadius="lg"
          overflow="hidden"
          borderWidth="1px"
          borderColor="border.subtle"
        >
          <CoverPageTrailer
            muted
            autoplay={false}
            trailerEmbedCode={form.values.trailer_embed_code}
          />
        </Box>
      ) : null}
      <Accordion.Root
        collapsible
        value={isAccordionOpen ? ["trailer"] : []}
        onValueChange={(details) => {
          const nextIsOpen = details.value.includes("trailer")
          setIsAccordionOpen(nextIsOpen)
          if (!nextIsOpen) {
            setIsReplacingTrailer(false)
            setEditorModeOverride(null)
          }
        }}
      >
        <Accordion.Item value="trailer" border="none">
          <Accordion.ItemTrigger
            p={0}
            cursor={isEditable ? "pointer" : "not-allowed"}
            disabled={!isEditable || form.isSubmitting}
          >
            <Text
              fontSize="sm"
              color={isEditable ? "PRIMARY_GRAY" : "fg.muted"}
              textDecoration="underline"
              m={0}
              textAlign="left"
            >
              <Pathicon
                icon={isAccordionOpen ? "check" : "video"}
                style={{ marginRight: "4px" }}
              />
              {isAccordionOpen
                ? t("header.close", { defaultValue: "Close" })
                : hasTrailer
                ? t("panel_header.change_video_overview", {
                    defaultValue: "Change the video overview"
                  })
                : t("panel_header.add_video_overview", {
                    defaultValue: "Add a video overview"
                  })}
            </Text>
          </Accordion.ItemTrigger>
          <Accordion.ItemContent pt={3}>
            <VStack align="stretch" gap={2}>
              <Text color="fg.muted" fontSize="sm" m={0}>
                {t("panel_header.trailer_embed_code_tip", {
                  defaultValue:
                    "The trailer replaces your Cover Page's background image and plays silently in the background."
                })}
              </Text>
              {!isEmbedReadOnly && visibleEditorMode === "url" ? (
                <Input
                  {...AUGMENTATION_PANEL_INPUT_PROPS}
                  value={trailerUrl}
                  onChange={(event) => setTrailerUrl(event.target.value)}
                  onBlur={() => void applyTrailerUrl()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      void applyTrailerUrl()
                    }
                  }}
                  placeholder={t("panel_header.paste_video_url", {
                    defaultValue: "Paste a video URL to embed..."
                  })}
                  disabled={!isEditable || form.isSubmitting || isResolvingUrl}
                />
              ) : null}
              {isEmbedReadOnly || visibleEditorMode === "embed" ? (
                <Field
                  {...AUGMENTATION_PANEL_TEXTAREA_PROPS}
                  as={Textarea}
                  name="trailer_embed_code"
                  placeholder={t("panel_header.paste_embed_code", {
                    defaultValue: "Paste embed code..."
                  })}
                  disabled={!isEditable || form.isSubmitting || isEmbedReadOnly}
                  minH={isEmbedReadOnly ? "3.5em" : "120px"}
                  maxH={isEmbedReadOnly ? "3.5em" : undefined}
                  resize={isEmbedReadOnly ? "none" : "vertical"}
                  overflow={isEmbedReadOnly ? "hidden" : undefined}
                  value={form.values.trailer_embed_code}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                    const nextEmbedCode = event.target.value
                    void form.setFieldValue("trailer_embed_code", nextEmbedCode)
                    if (trailerUrl) {
                      setTrailerUrl("")
                    }
                  }}
                />
              ) : null}
              <HStack w="full" gap={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="plain"
                  w="max-content"
                  px={0}
                  py={0}
                  h="auto"
                  minW="unset"
                  fontWeight="normal"
                  color={isEditable ? "PRIMARY_GRAY" : "fg.muted"}
                  textDecoration="underline"
                  onClick={() => {
                    if (isEmbedReadOnly) {
                      setIsReplacingTrailer(true)
                      setEditorModeOverride("embed")
                      return
                    }

                    if (visibleEditorMode === "url") {
                      setEditorModeOverride("embed")
                      return
                    }

                    setEditorModeOverride("url")
                  }}
                  disabled={!isEditable || form.isSubmitting}
                  gap={1}
                >
                  <Pathicon
                    icon={
                      isEmbedReadOnly
                        ? "recur"
                        : visibleEditorMode === "url"
                        ? "link"
                        : "code"
                    }
                  />
                  {isEmbedReadOnly
                    ? t("panel_header.replace_video_code", {
                        defaultValue: "Replace video code"
                      })
                    : visibleEditorMode === "url"
                    ? t("panel_header.or_paste_full_embed_code", {
                        defaultValue: "Or paste a full embed code"
                      })
                    : t("panel_header.use_video_url_instead", {
                        defaultValue: "Use a video URL instead"
                      })}
                </Button>
              </HStack>
            </VStack>
          </Accordion.ItemContent>
        </Accordion.Item>
      </Accordion.Root>
    </VStack>
  )
}

function HeaderImagesSection() {
  const resource = useCoverPageContext((value) => value.resource)
  const form = useFormikContext<CoverPagePanelHeaderValues>()
  const isEditable = useCoverPageIsEditable()
  const { t } = useCoverPageTranslate()
  const previewResource = useMemo(() => {
    if (!resource) return null

    const hasBackgroundImageChanges =
      form.values.background_image !== form.initialValues.background_image &&
      !isCoverPageResolvedBackgroundImageUrl(
        resource,
        form.values.background_image
      )
    const backgroundImage = hasBackgroundImageChanges
      ? form.values.background_image || undefined
      : !form.values.is_ambient_background
      ? getCoverPagePristineBackgroundImage(resource) || undefined
      : resource.background_image || undefined
    const hasBackgroundOverlayChanges =
      form.values.background_overlay !== form.initialValues.background_overlay
    const backgroundOverlay = hasBackgroundOverlayChanges
      ? form.values.background_overlay || undefined
      : resource.background_overlay || undefined

    return {
      ...resource,
      ...getCoverPageAmbientResourceFields(form.values.is_ambient_background),
      background_image: backgroundImage || null,
      cover_background_image: backgroundImage || null,
      background_overlay: backgroundOverlay || null
    }
  }, [
    form.initialValues.background_image,
    form.initialValues.background_overlay,
    form.values.background_image,
    form.values.background_overlay,
    form.values.is_ambient_background,
    resource
  ])

  if (!resource || !previewResource) return null

  const coverImage = getCoverPageDisplayImage({
    cover_display_image: form.values.cover_image || resource.cover_display_image,
    cover_image: resource.cover_image,
    image_raw: resource.image_raw
  })

  return (
    <VStack align="stretch" gap={3}>
      <Box position="relative" mx={-2} mt={-2} overflow="hidden">
          <ResourceBackground
          resource={previewResource}
          position="center"
          forceBlur={form.values.is_ambient_background}
          style={{
            position: "absolute",
            inset: 0
          }}
        />
        <Box position="absolute" top={3} right={3} zIndex={2}>
          <CoverPageBackgroundSettingsPopover
            contextKey={`resource-${resource.id}`}
            isEditable={!!isEditable}
            disabled={!isEditable || form.isSubmitting}
            backgroundOverlay={form.values.background_overlay}
            savedBackgroundOverlay={resource.background_overlay || ""}
            isAmbient={form.values.is_ambient_background}
            onChooseBackgroundImage={(url) => {
              void form.setFieldValue("background_image", url)
            }}
            onChangeBackgroundOverlay={(overlay) => {
              void form.setFieldValue("background_overlay", overlay)
            }}
            onChangeAmbient={(isAmbient) => {
              void form.setFieldValue("is_ambient_background", isAmbient)
            }}
          >
            <Button
              size="sm"
              // @ts-expect-error
              variant="blurredLightGray"
              cursor="pointer"
              disabled={!isEditable || form.isSubmitting}
            >
              <Pathicon icon="edit" />
              {t("panel_header.background", { defaultValue: "Background" })}
            </Button>
          </CoverPageBackgroundSettingsPopover>
        </Box>
        <VStack
          position="relative"
          zIndex={1}
          justify="center"
          minH={{ base: "220px", md: "250px" }}
          px={"30%"}
          py={"12.5%"}
          gap={3}
        >
          <HeaderMediaTrigger
            contextKey={`resource-${resource.id}`}
            onChoose={(url) => {
              void form.setFieldValue("cover_image", url)
            }}
            renderTrigger={({ isLoading, onClick }) => (
              <VStack
                gap={3}
                position="relative"
                justify="center"
                w="100%"
                maxW="220px"
              >
                <Button
                  onClick={onClick}
                  disabled={!isEditable || form.isSubmitting || isLoading}
                  borderRadius="10px"
                  overflow="hidden"
                  boxShadow="0 8px 24px rgba(0, 0, 0, 0.22)"
                  h="auto"
                  w="100%"
                  bg="white"
                  p={0}
                >
                  <HeaderCoverImagePreview
                    image={coverImage}
                    alt={t("panel_header.cover_image_preview_alt", {
                      defaultValue: "Cover image preview"
                    })}
                  />
                </Button>
                <Button
                  size="xs"
                  position="absolute"
                  top={2}
                  right={2}
                  // @ts-expect-error
                  variant="blurredLightGray"
                  onClick={onClick}
                  disabled={!isEditable || form.isSubmitting || isLoading}
                  loading={isLoading}
                >
                  <Pathicon icon="recur" />
                  {t("panel_header.cover", { defaultValue: "Cover" })}
                </Button>
              </VStack>
            )}
          />
        </VStack>
      </Box>
    </VStack>
  )
}

function HeaderTitleAndSummarySection() {
  const form = useFormikContext<CoverPagePanelHeaderValues>()
  const isEditable = useCoverPageIsEditable()
  const { t } = useCoverPageTranslate()

  return (
    <VStack align="stretch" gap={4}>
      <Field
        {...AUGMENTATION_PANEL_INPUT_PROPS}
        name="name"
        label={t("panel_header.path_title_label", {
          defaultValue: "Path Title"
        })}
        placeholder={t("panel_header.path_title_placeholder", {
          defaultValue: "Enter the Path name"
        })}
        disabled={!isEditable || form.isSubmitting}
      />
      <Field
        {...AUGMENTATION_PANEL_TEXTAREA_PROPS}
        as={Textarea}
        name="short_description"
        label={t("panel_header.path_summary_label", {
          defaultValue: "Path summary"
        })}
        placeholder={t("panel_header.path_summary_placeholder", {
          defaultValue:
            "In one sentence, why should someone complete this Path?"
        })}
        disabled={!isEditable || form.isSubmitting}
        minH="120px"
        resize="vertical"
      />
      <Text
        fontSize="xs"
        color="fg.muted"
        textAlign="right"
        m={0}
      >{`${form.values.short_description.length}/300`}</Text>
    </VStack>
  )
}

function HeaderTip() {
  const { t } = useCoverPageTranslate()

  return (
    <HStack
      align="start"
      gap={3}
      p={3}
      borderRadius="xl"
      bg="blackAlpha.50"
      borderWidth="1px"
      borderColor="blackAlpha.100"
    >
      <Box pt="2px" color="yellow.500">
        <Pathicon icon="lightbulb-on-fill" />
      </Box>
      <Text fontSize="sm" color="fg.muted" m={0}>
        {t("panel_header.tip", {
          defaultValue:
            "Changes to the Path title and images apply to both the Cover Page and the Path."
        })}
      </Text>
    </HStack>
  )
}

export function CoverPagePanelHeaderRoute() {
  const resource = useCoverPageContext((value) => value.resource)
  const isEditable = useCoverPageIsEditable()
  const [updateCoverPageResource] = useUpdateCoverPageResourceMutation()
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()

  const initialValues = useMemo(
    () => (resource ? buildInitialValues(resource) : null),
    [resource]
  )

  const validate = useMemo(
    () =>
      toFormikValidate(
        headerSchema.extend({
          name: z.string().refine(
            (value) => value.trim().length >= PATH_TITLE_MIN_LENGTH,
            (value) => {
              if (value.trim().length === 0) {
                return {
                  message: t("panel_header.path_title_required", {
                    defaultValue: "Path Title is required."
                  })
                }
              }

              return {
                message: t("panel_header.path_title_min_length", {
                  defaultValue:
                    "The title should be at least {{count}} characters long.",
                  count: PATH_TITLE_MIN_LENGTH
                })
              }
            }
          ),
          short_description: z.string().max(
            300,
            t("panel_header.path_summary_max_length", {
              defaultValue: "Path summary must be 300 characters or fewer."
            })
          )
        })
      ),
    [t]
  )

  if (!resource || !initialValues) return <></>

  const handleSubmit = async (values: CoverPagePanelHeaderValues) => {
    try {
      if (!isEditable) return

      const resourceType = RESOURCE_TYPE_LABELS[resource.resource_type]

      if (!resourceType) {
        throw new Error(
          `Unsupported resource type ${resource.resource_type} for header updates.`
        )
      }

      const hasAmbientChanges =
        values.is_ambient_background !== initialValues.is_ambient_background

      const hasBackgroundImageChanges =
        values.background_image !== initialValues.background_image &&
        !isCoverPageResolvedBackgroundImageUrl(
          resource,
          values.background_image
        )
      const hasCoverImageChanges =
        values.cover_image !== initialValues.cover_image
      const hasImageChanges = hasCoverImageChanges || hasBackgroundImageChanges

      const hasChanges =
        values.name !== initialValues.name ||
        values.short_description !== initialValues.short_description ||
        hasImageChanges ||
        hasAmbientChanges ||
        values.background_overlay !== initialValues.background_overlay ||
        values.trailer_embed_code !== initialValues.trailer_embed_code

      if (!hasChanges) return

      const backgroundType = getCoverPageBackgroundTypeInput(
        values.is_ambient_background
      )
      const ambientResourceFields = getCoverPageAmbientResourceFields(
        values.is_ambient_background
      )
      const contributorPositioningContext = getContributorPositioningContext(
        resource.id
      )
      const categoryPositioningContext = getCategoryPositioningContext(
        resource.id
      )
      const optimisticBackgroundImage = hasBackgroundImageChanges
        ? values.background_image || null
        : !values.is_ambient_background
        ? getCoverPagePristineBackgroundImage(resource)
        : resource.background_image || null
      const optimisticBackgroundImageInput = hasBackgroundImageChanges
        ? values.background_image || null
        : resource.background_image_raw || null
      const optimisticPristineBackground = hasBackgroundImageChanges
        ? values.background_image || null
        : getCoverPagePristineBackgroundImage(resource)
      const optimisticBlurredBackground = hasBackgroundImageChanges
        ? values.background_image || null
        : resource.cover_blurred_background || optimisticBackgroundImage

      return updateCoverPageResource({
        variables: {
          id: resource.id,
          name: values.name,
          resource_type: resourceType,
          contributorPositioningContext,
          categoryPositioningContext,
          short_description: values.short_description,
          ...(values.cover_image !== initialValues.cover_image
            ? {
                cover_image: values.cover_image || null
              }
            : {}),
          ...(hasBackgroundImageChanges
            ? {
                background_image: values.background_image || null,
                background_type: backgroundType
              }
            : {}),
          ...(values.background_overlay !== initialValues.background_overlay
            ? {
                background_overlay: values.background_overlay || null,
                background_type: backgroundType
              }
            : {}),
          ...(hasAmbientChanges
            ? {
                background_type: backgroundType
              }
            : {}),
          ...(values.trailer_embed_code !== initialValues.trailer_embed_code
            ? {
                trailer_embed_code: values.trailer_embed_code || ""
              }
            : {})
        },
        optimisticResponse: {
          __typename: "Mutations",
          updateResource: {
            __typename: "Resource",
            ...resource,
            ...values,
            ...ambientResourceFields,
            background_overlay: values.background_overlay || null,
            background_image: optimisticBackgroundImage,
            cover_background_image: optimisticBackgroundImage,
            background_image_raw: optimisticBackgroundImageInput,
            pristine_background: optimisticPristineBackground,
            cover_blurred_background: values.is_ambient_background
              ? optimisticBlurredBackground
              : resource.cover_blurred_background || null,
            cover_display_image: values.cover_image || resource.cover_display_image,
            cover_image: values.cover_image || resource.cover_image,
            image: values.cover_image || resource.image || "",
            image_raw: values.cover_image || resource.image_raw,
            trailer_embed_code: values.trailer_embed_code || ""
          }
        }
      })
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          t("panel_errors.save_header", {
            defaultValue: "Error saving Cover Page's header:"
          })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={["cover-page", "header", resource.id]}
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      enableReinitialize
      formStateOnSubmit="preserve"
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelHeaderValues>) => {
        const formId = "cover-page-panel-header"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="15px" w="full">
              <HeaderImagesSection />
              <HeaderTitleAndSummarySection />
              <HeaderTrailerSection />
              <HeaderTip />
            </VStack>
            <CoverPagePanelFormActions
              form={form}
              formId={formId}
              disabled={!isEditable}
              submit={{
                children: t("panel.apply_changes", {
                  defaultValue: "Apply changes"
                })
              }}
              discard={{
                children: t("panel.discard_changes", {
                  defaultValue: "Discard changes"
                })
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}
